import { useNavigate } from 'react-router-dom';
import {
  Container,
  Title,
  Text,
  Stack,
  Group,
  Avatar,
  UnstyledButton,
  Button,
  Loader,
  Center,
  Alert,
  Divider,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconHeart,
  IconUserPlus,
  IconMessageCircle,
  IconBell,
  IconChecks,
  IconUser,
  IconTrophy,
  IconThumbUp,
} from '@tabler/icons-react';
import { notifications as toasts } from '@mantine/notifications';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useInfiniteScroll } from '../../hooks/useInfiniteScroll';
import { notificationsService, type Notification } from '../../services/notifications';
import { useAuth } from '../../context/AuthContext';
import { formatRelativeDate } from '../../utils/dateUtils';

const PAGE_SIZE = 20;

function getNotificationIcon(typeName: string) {
  switch (typeName) {
    case 'Like': return <IconHeart size={16} color="var(--mantine-color-red-6)" />;
    case 'Follow': return <IconUserPlus size={16} color="var(--mantine-color-blue-6)" />;
    case 'Comment': return <IconMessageCircle size={16} color="var(--mantine-color-green-6)" />;
    case 'BadgeEarned': return <IconTrophy size={16} color="var(--mantine-color-yellow-6)" />;
    case 'HelpfulVote': return <IconThumbUp size={16} color="var(--mantine-color-teal-6)" />;
    default: return <IconBell size={16} color="var(--mantine-color-gray-6)" />;
  }
}

function getNotificationText(notification: Notification): string {
  const actor = notification.actor?.username ? `@${notification.actor.username}` : 'Someone';
  switch (notification.typeName) {
    case 'Like': return `${actor} liked your review`;
    case 'Follow': return `${actor} started following you`;
    case 'Comment': return `${actor} commented on your review`;
    case 'BadgeEarned':
    case 'HelpfulVote':
    case 'System':
    default:
      return notification.message || 'New notification';
  }
}


function getDateGroup(dateString: string): string {
  const date = new Date(dateString);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  const weekAgo = new Date(today);
  weekAgo.setDate(today.getDate() - 7);

  const dateOnly = new Date(date);
  dateOnly.setHours(0, 0, 0, 0);

  if (dateOnly.getTime() === today.getTime()) return 'Today';
  if (dateOnly.getTime() === yesterday.getTime()) return 'Yesterday';
  if (dateOnly >= weekAgo) return 'This week';
  return 'Earlier';
}

function groupNotifications(items: Notification[]): { label: string; items: Notification[] }[] {
  const order = ['Today', 'Yesterday', 'This week', 'Earlier'];
  const map = new Map<string, Notification[]>();

  for (const n of items) {
    const label = getDateGroup(n.createdAt);
    if (!map.has(label)) map.set(label, []);
    map.get(label)!.push(n);
  }

  return order.filter(label => map.has(label)).map(label => ({ label, items: map.get(label)! }));
}

export default function Notifications() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const queryClient = useQueryClient();

  const {
    data,
    isLoading,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['notifications'],
    queryFn: ({ pageParam = 1 }) =>
      notificationsService.getNotifications(pageParam as number, PAGE_SIZE),
    getNextPageParam: (lastPage, pages) => {
      const loaded = pages.reduce((acc, p) => acc + p.notifications.length, 0);
      return loaded < lastPage.totalCount ? pages.length + 1 : undefined;
    },
    initialPageParam: 1,
    enabled: isAuthenticated,
    staleTime: 60_000,
  });

  const allItems: Notification[] = data?.pages.flatMap((p) => p.notifications) ?? [];
  const unreadCount = data?.pages[0]?.unreadCount ?? 0;
  const groups = groupNotifications(allItems);
  const sentinelRef = useInfiniteScroll(fetchNextPage, !!hasNextPage, isFetchingNextPage);

  const patchPages = (fn: (n: Notification) => Notification, newUnread?: (prev: number) => number) => {
    queryClient.setQueryData(['notifications'], (old: typeof data) => {
      if (!old) return old;
      return {
        ...old,
        pages: old.pages.map((page, i) => ({
          ...page,
          notifications: page.notifications.map(fn),
          unreadCount: i === 0 && newUnread ? newUnread(page.unreadCount) : page.unreadCount,
        })),
      };
    });
  };

  const markAllReadMutation = useMutation({
    mutationFn: () => notificationsService.markAllAsRead(),
    onSuccess: () => patchPages((n) => ({ ...n, isRead: true }), () => 0),
    onError: () => {
      toasts.show({ title: 'Error', message: 'Failed to mark notifications as read', color: 'red' });
    },
  });

  const markReadMutation = useMutation({
    mutationFn: (notificationId: string) => notificationsService.markAsRead(notificationId),
    onSuccess: (_data, notificationId) =>
      patchPages(
        (n) => (n.id === notificationId ? { ...n, isRead: true } : n),
        (prev) => Math.max(0, prev - 1),
      ),
  });

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.isRead) markReadMutation.mutate(notification.id);
    if (notification.typeName === 'Follow' && notification.actor) {
      navigate(`/users/${notification.actor.username}`);
    } else if (notification.reviewId) {
      navigate(`/reviews/${notification.reviewId}`);
    }
  };

  if (!isAuthenticated) {
    return (
      <Container fluid px="xl" pt="md" pb="xl">
        <Alert icon={<IconAlertCircle size={16} />} color="blue">
          Please log in to view your notifications.
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid px="xl" pt="md" pb="xl">
      <Group justify="space-between" mb="lg">
        <Title order={2}>Notifications</Title>
        {unreadCount > 0 && (
          <Button
            variant="subtle"
            size="compact-sm"
            leftSection={<IconChecks size={14} />}
            loading={markAllReadMutation.isPending}
            onClick={() => markAllReadMutation.mutate()}
          >
            Mark all read
          </Button>
        )}
      </Group>

      {isLoading ? (
        <Center py="xl">
          <Loader size="lg" />
        </Center>
      ) : error ? (
        <Alert icon={<IconAlertCircle size={16} />} color="red" title="Error">
          Failed to load notifications
          <Button variant="subtle" size="xs" mt="xs" onClick={() => refetch()}>Retry</Button>
        </Alert>
      ) : allItems.length === 0 ? (
        <Center py="xl">
          <Stack align="center" gap="xs">
            <IconBell size={48} color="var(--mantine-color-dimmed)" />
            <Text c="dimmed">No notifications yet</Text>
          </Stack>
        </Center>
      ) : (
        <Stack gap="lg">
          {groups.map((group, gi) => (
            <Stack key={group.label} gap={0}>
              <Text
                size="xs"
                fw={700}
                tt="uppercase"
                c="dimmed"
                px="md"
                pb="xs"
                style={{ letterSpacing: '0.05em' }}
              >
                {group.label}
              </Text>
              <Stack gap={0}>
                {group.items.map((notification, i) => (
                  <UnstyledButton
                    key={notification.id}
                    onClick={() => handleNotificationClick(notification)}
                    py="sm"
                    px="md"
                    style={{
                      borderBottom: i < group.items.length - 1 ? '1px solid var(--mantine-color-default-border)' : 'none',
                      backgroundColor: notification.isRead ? 'transparent' : 'light-dark(var(--mantine-color-blue-0), var(--mantine-color-dark-6))',
                      borderRadius: 'var(--mantine-radius-sm)',
                      transition: 'background-color 150ms ease',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--mantine-color-default-hover)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = notification.isRead
                        ? 'transparent'
                        : 'light-dark(var(--mantine-color-blue-0), var(--mantine-color-dark-6))';
                    }}
                  >
                    <Group gap="sm" wrap="nowrap">
                      <Avatar
                        src={notification.typeName === 'BadgeEarned' || notification.typeName === 'HelpfulVote' ? undefined : notification.actor?.profileImageUrl}
                        size="md"
                        radius="xl"
                        color={notification.typeName === 'BadgeEarned' ? 'yellow' : notification.typeName === 'HelpfulVote' ? 'teal' : undefined}
                      >
                        {notification.typeName === 'BadgeEarned'
                          ? <IconTrophy size={18} />
                          : notification.typeName === 'HelpfulVote'
                          ? <IconThumbUp size={18} />
                          : <IconUser size={18} />}
                      </Avatar>
                      <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
                        <Group gap={6} wrap="nowrap">
                          {getNotificationIcon(notification.typeName)}
                          <Text size="sm" lineClamp={2}>{getNotificationText(notification)}</Text>
                        </Group>
                        <Text size="xs" c="dimmed">{formatRelativeDate(notification.createdAt)}</Text>
                      </Stack>
                      {!notification.isRead && (
                        <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: 'var(--mantine-color-blue-filled)', flexShrink: 0 }} />
                      )}
                    </Group>
                  </UnstyledButton>
                ))}
              </Stack>
              {gi < groups.length - 1 && <Divider mt="sm" />}
            </Stack>
          ))}

          <div ref={sentinelRef} />
          {isFetchingNextPage && <Center py="sm"><Loader size="sm" /></Center>}
        </Stack>
      )}
    </Container>
  );
}
